import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@core/services/toast.service';
import { InputType } from '@core/enums/input-type';
import { Subject, takeUntil } from 'rxjs';
import {
  DEFAULT_MODULE_TIMEOUT_SECONDS,
  getModuleByIds,
  getModulesCourseById,
  IModuleDefinition,
  IModulesCourseDefinition
} from '../../data/course-modules.data';
import { ModuleFlowService } from '../../services/module-flow.service';
import { ModuleProgressService } from '../../services/module-progress.service';
import { ModuleJournalService } from '../../services/module-journal.service';
import { CertificateService } from '../../services/certificate.service';
import { AccessibleConfirmService } from '../../services/accessible-confirm.service';
import { IModuleJournal, IModuleJournalResponse, IModuleNotebookSection } from '../../interfaces/imodule-journal';

@Component({
  selector: 'prism-module-view',
  templateUrl: './module-view.component.html',
  styleUrl: './module-view.component.scss'
})
export class ModuleViewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private sanitizer = inject(DomSanitizer);
  private toastService = inject(ToastService);
  private confirmService = inject(AccessibleConfirmService);
  private progressService = inject(ModuleProgressService);
  private journalService = inject(ModuleJournalService);
  private certificateService = inject(CertificateService);
  private flow = inject(ModuleFlowService);
  private cdr = inject(ChangeDetectorRef);
  private el = inject(ElementRef);
  private destroy$ = new Subject<void>();
  private inertedElements: HTMLElement[] = [];
  @ViewChild('notebookDialog') notebookDialog?: ElementRef<HTMLElement>;

  InputType = InputType;

  course?: IModulesCourseDefinition;
  module?: IModuleDefinition;
  courseId = 0;
  moduleId = '';

  iframeUrl?: SafeResourceUrl;
  journalForm!: FormGroup;
  completeForm!: FormGroup;

  // notebook modal state
  isNotebookVisible = false;
  showEntryForm = false;
  editingEntryId: number | null = null;
  entries: IModuleJournalResponse[] = [];

  // Reflection-question section for this module, fetched from the DB (lookup.journalSections).
  notebookSection?: IModuleNotebookSection;

  // completion state
  hasModuleStarted = false;
  moduleStartTime: number | null = null;
  isModuleCompleted = false;
  alreadyCompleted = false;
  isSubmitting = false;
  private progressIntervalId: any = null;

  remainingSeconds = 0;
  private timerIntervalId: any = null;

  // observer for the in-iframe accessibility fixes (see injectIframeAccessibilityFixes)
  private iframeDirObserver: MutationObserver | null = null;

  ngOnInit(): void {
    this.journalForm = this.fb.group({
      entry: ['', [Validators.required]]
    });
    this.completeForm = this.fb.group({
      isCompleted: [false]
    });

    this.route.params.subscribe(params => {
      this.courseId = +params['courseId'];
      this.moduleId = params['moduleId'];
      this.course = getModulesCourseById(this.courseId);
      this.module = getModuleByIds(this.courseId, this.moduleId);

      if (!this.module) {
        this.toastService.error('Module not found');
        window.location.href = `/course-modules/${this.courseId}`;
        return;
      }

      this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.module.iframeSrc);

      this.progressService.loadCourseProgress(this.courseId).subscribe(() => {
        const saved = this.progressService.getProgress(this.courseId, this.moduleId);
        this.alreadyCompleted = saved.isCompleted;
        this.completeForm.patchValue({ isCompleted: saved.isCompleted });

        if (saved.isCompleted) {
          this.isModuleCompleted = true;
        } else {
          this.startCountdownTicker();
        }
        this.cdr.markForCheck();
      });

      this.loadEntries();
      this.loadNotebookSection();
      // Iframe progress monitoring is wired via the iframe's (load) event in the template.
    });
  }

  private loadNotebookSection(): void {
    this.journalService.getNotebookSections(this.courseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(sections => {
        this.notebookSection = this.matchNotebookSection(sections);
        this.cdr.markForCheck();
      });
  }

  private matchNotebookSection(sections: IModuleNotebookSection[]): IModuleNotebookSection | undefined {
    const moduleKey = this.moduleId.toUpperCase();

    const byModuleId = sections.find(s => (s.moduleId ?? '').trim().toUpperCase() === moduleKey);
    if (byModuleId) return byModuleId;

    const nameSuffix = `${this.courseId}${moduleKey}`;
    return sections.find(s => (s.sectionName ?? '').trim().toUpperCase().endsWith(nameSuffix));
  }

  get moduleTimeoutSeconds(): number {
    return this.module?.timeoutSeconds ?? DEFAULT_MODULE_TIMEOUT_SECONDS;
  }

  get hasMinimumStay(): boolean {
    return this.moduleTimeoutSeconds > 0;
  }

  // ---- visible countdown (drives the "Time Remaining" display) ----
  // Counts down the same minimum-stay window used for completion: it shows the
  // full duration until the module starts, then ticks down from moduleStartTime.

  private startCountdownTicker(): void {
    if (this.timerIntervalId !== null || this.alreadyCompleted || !this.hasMinimumStay) return;
    this.updateRemaining();
    this.timerIntervalId = setInterval(() => {
      this.updateRemaining();
      this.cdr.markForCheck();
    }, 1000);
  }

  private updateRemaining(): void {
    if (this.moduleStartTime === null) {
      this.remainingSeconds = this.moduleTimeoutSeconds;
      return;
    }
    const timeoutMs = this.moduleTimeoutSeconds * 1000;
    const elapsed = Date.now() - this.moduleStartTime;
    this.remainingSeconds = Math.max(0, Math.ceil((timeoutMs - elapsed) / 1000));

    if (this.remainingSeconds === 0 && this.timerIntervalId !== null) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
  }

  get timerComplete(): boolean {
    return this.remainingSeconds === 0;
  }

  get minStayDuration(): string {
    const seconds = this.moduleTimeoutSeconds;
    if (seconds < 60) {
      return `${seconds} second${seconds === 1 ? '' : 's'}`;
    }
    const minutes = Math.round(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  get timerHours(): string {
    return this.pad(Math.floor(this.remainingSeconds / 3600));
  }

  get timerMinutes(): string {
    return this.pad(Math.floor((this.remainingSeconds % 3600) / 60));
  }

  get timerSeconds(): string {
    return this.pad(this.remainingSeconds % 60);
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  // ---- iframe load ----

  onIframeLoad(): void {
    const iframe = document.getElementById('module-iframe') as HTMLIFrameElement | null;
    if (iframe) {
      this.injectIframeAccessibilityFixes(iframe);
    }
    this.startMonitoringModuleProgress();
  }

  // The Rise module packages are third-party content we don't modify, so these
  // accessibility fixes are injected into the (same-origin) iframe at runtime:
  //  1. dir: Rise sets an invalid 'dir' value at runtime; normalize it to ltr.
  //  2. Nested headings: Rise renders an <h2> that wraps another <h2>, so the
  //     outer heading's text duplicates the inner one ("duplicate headings").
  //     Strip the outer heading's semantics (only ever wraps when invalid).
  private injectIframeAccessibilityFixes(iframe: HTMLIFrameElement): void {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';

    // The sidebar header title + progress text are white, layered (z-index) over
    // a separate absolutely-positioned graphic that carries the theme color.
    // Contrast checkers only walk the ancestor background chain, so they see
    // white-on-white (~1.04). Give the text's own container the theme-color
    // background so the checker reads it — visually identical when the header
    // is showing the theme color rather than a cover image.
    const STYLE_ID = 'prism-a11y-fixes';
    if (!doc.getElementById(STYLE_ID)) {
      const style = doc.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        .nav-sidebar-header__content {
          background-color: var(--color-theme, #2f5597) !important;
        }
      `;
      (doc.head || doc.documentElement).appendChild(style);
    }

    const applyFixes = () => {
      doc.querySelectorAll('[dir]').forEach(element => {
        const value = (element.getAttribute('dir') || '').trim().toLowerCase();
        if (value !== 'ltr' && value !== 'rtl' && value !== 'auto') {
          element.setAttribute('dir', 'ltr');
        }
      });

      doc.querySelectorAll(HEADING_SELECTOR).forEach(heading => {
        if (heading.getAttribute('role') !== 'presentation' && heading.querySelector(HEADING_SELECTOR)) {
          heading.setAttribute('role', 'presentation');
        }
      });
    };

    applyFixes();
    this.iframeDirObserver?.disconnect();
    this.iframeDirObserver = new MutationObserver(applyFixes);
    this.iframeDirObserver.observe(doc.documentElement, {
      attributes: true,
      attributeFilter: ['dir'],
      childList: true,
      subtree: true
    });
  }

  // ---- iframe progress monitoring (mirrors course-view) ----

  startMonitoringModuleProgress(): void {
    if (this.alreadyCompleted || this.progressIntervalId !== null) return;

    const iframe = document.getElementById('module-iframe') as HTMLIFrameElement | null;
    if (!iframe) {
      this.toastService.error('There was an issue loading the module. Please refresh the page and try again.');
      return;
    }

    this.progressIntervalId = setInterval(() => {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      const progressText = iframeDocument
        ?.querySelector('.nav-sidebar-header__progress-text')
        ?.innerHTML?.trim();

      if (!this.hasModuleStarted && progressText === '0% COMPLETE') {
        this.hasModuleStarted = true;
        this.moduleStartTime = Date.now();
      }

      const moduleTimeoutMs = this.moduleTimeoutSeconds * 1000;
      const hasTimedOut = this.moduleStartTime && (Date.now() - this.moduleStartTime) >= moduleTimeoutMs;

      if (hasTimedOut && progressText === '100% COMPLETE') {
        clearInterval(this.progressIntervalId);
        this.progressIntervalId = null;
        this.isModuleCompleted = true;
        this.cdr.markForCheck();
      }
    }, 3000);
  }

  // ---- notebook modal ----

  loadEntries(): void {
    this.journalService.getByModuleId(this.courseId, this.moduleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(entries => {
        this.entries = entries
          .map(e => ({ ...e, dateSubmitted: new Date(e.dateSubmitted) }))
          .sort((a, b) => b.dateSubmitted.getTime() - a.dateSubmitted.getTime());
        this.cdr.markForCheck();
      });
  }

  openNotebook(): void {
    this.loadEntries();
    this.isNotebookVisible = true;
    this.showEntryForm = false;
    this.editingEntryId = null;
    this.journalForm.reset();
    this.setBackgroundInert(true);
    setTimeout(() => this.notebookDialog?.nativeElement.focus());
  }

  closeNotebook(): void {
    this.isNotebookVisible = false;
    this.showEntryForm = false;
    this.editingEntryId = null;
    this.journalForm.reset();
    this.setBackgroundInert(false);
  }

  private setBackgroundInert(hide: boolean): void {
    if (hide) {
      this.inertedElements = [];
      let node: HTMLElement | null = this.el.nativeElement;
      while (node && node !== document.body) {
        const parent = node.parentElement;
        if (parent) {
          Array.from(parent.children).forEach(sibling => {
            if (sibling !== node && sibling instanceof HTMLElement && !sibling.hasAttribute('inert')) {
              sibling.setAttribute('inert', '');
              this.inertedElements.push(sibling);
            }
          });
        }
        node = parent;
      }
    } else {
      this.inertedElements.forEach(el => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  showAddEntryForm(): void {
    this.editingEntryId = null;
    this.journalForm.reset();
    this.showEntryForm = true;
  }

  editEntry(event: Event, entry: IModuleJournalResponse): void {
    event.stopPropagation();
    this.editingEntryId = entry.id;
    this.journalForm.patchValue({ entry: entry.journalEntry });
    this.showEntryForm = true;
  }

  cancelEntry(event: Event): void {
    event.stopPropagation();
    this.showEntryForm = false;
    this.editingEntryId = null;
    this.journalForm.reset();
  }

  submitEntry(): void {
    const minLength = this.module?.minEntryLimit ?? 1;
    const entryText: string = this.journalForm.get('entry')?.value ?? '';

    if (entryText.trim().length < minLength) {
      this.toastService.error(`Notebook Entry does not meet the minimum characters (${minLength} characters)`);
      return;
    }
    if (!this.journalForm.valid) return;

    const payload: IModuleJournal = {
      courseId: this.courseId,
      moduleId: this.moduleId,
      journalEntry: entryText
    };

    if (this.editingEntryId) {
      this.journalService.editJournal({ ...payload, id: this.editingEntryId })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadEntries();
          this.closeNotebook();
        });
    } else {
      this.journalService.addJournal(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadEntries();
          this.closeNotebook();
        });
    }
  }

  deleteEntry(event: Event): void {
    event.stopPropagation();
    const id = this.editingEntryId;
    if (!id) {
      this.toastService.error('Notebook entry not found');
      return;
    }
    this.confirmService.confirm({
      nzTitle: 'Delete Entry',
      nzContent: 'Are you sure you want to delete this entry?',
      nzOkText: 'Yes',
      nzCancelText: 'No',
      nzIconType: '',
      nzClosable: false,
      nzOnOk: () => {
        this.journalService.deleteJournal(id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(deleted => {
            if (deleted) {
              this.toastService.success('Entry deleted successfully');
              this.editingEntryId = null;
              this.showEntryForm = false;
              this.journalForm.reset();
              this.loadEntries();
            } else {
              this.toastService.error('Error deleting entry');
            }
          });
      }
    });
  }

  // ---- form helpers ----

  get entry() {
    return this.journalForm.get('entry');
  }

  get isCompletedControl() {
    return this.completeForm.get('isCompleted');
  }

  get hasAtLeastOneEntry(): boolean {
    return this.entries.length > 0;
  }

  get requiresNotebookEntry(): boolean {
    return this.module?.requiresNotebookEntry !== false;
  }

  get notebookDescription(): string {
    return this.notebookSection?.description ?? '';
  }

  get hasNotebookQuestions(): boolean {
    return !!this.notebookSection && !!this.notebookSection.description;
  }

  get requiresAcknowledgement(): boolean {
    return this.module?.requiresAcknowledgement !== false;
  }

  get submitButtonText(): string {
    return this.requiresAcknowledgement ? 'Agree and Submit' : 'Complete';
  }

  get canSubmit(): boolean {
    return this.isModuleCompleted
      && (!this.requiresNotebookEntry || this.hasAtLeastOneEntry)
      && (!this.requiresAcknowledgement || !!this.isCompletedControl?.value)
      && !this.alreadyCompleted
      && !this.isSubmitting;
  }

  get submitDisabledReason(): string {
    if (this.alreadyCompleted) return 'Module already completed';
    if (!this.isModuleCompleted) return 'Please complete the module content first';
    if (this.requiresNotebookEntry && !this.hasAtLeastOneEntry) return 'Please add at least one PRISM Digital Notebook Entry';
    if (this.requiresAcknowledgement && !this.isCompletedControl?.value) return 'Please check the acknowledgement box';
    return '';
  }

  handleCheckbox(): void {
    const current = this.isCompletedControl?.value;
    this.isCompletedControl?.setValue(!current);
  }

  // ---- submit / nav ----

  submit(): void {
    if (this.isSubmitting) return;
    if (!this.canSubmit) {
      this.toastService.error(this.submitDisabledReason || 'Please complete all requirements first');
      return;
    }

    this.isSubmitting = true;
    this.progressService.saveProgress(this.courseId, this.moduleId, {
      isCompleted: true,
      completedAt: new Date().toISOString()
    });
    this.toastService.success(`${this.module?.name} completed and saved.`);
    this.alreadyCompleted = true;

    // Check if all modules are now complete and try to award certificate
    const moduleIds = this.course?.modules.map(m => m.id) ?? [];
    if (this.progressService.isCourseComplete(this.courseId, moduleIds)) {
      this.certificateService.tryAwardCertificate(this.courseId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(cert => {
          this.isSubmitting = false;
          if (cert) {
            this.toastService.success('Congratulations! You have earned your certificate.');
          }
          this.flow.passPopup();
          window.location.href = `/course-modules/${this.courseId}/list`;
        });
    } else {
      this.isSubmitting = false;
      this.flow.passPopup();
      window.location.href = `/course-modules/${this.courseId}/list`;
    }
  }

  goBackToList(): void {
    if (this.alreadyCompleted) {
      this.flow.passPopup();
      window.location.href = `/course-modules/${this.courseId}/list`;
      return;
    }

    this.confirmService.confirm({
      nzTitle: 'Leave this module?',
      nzContent: `If you go back now, your progress within ${this.module?.name} will restart from the beginning. Your saved notebook entries will remain.`,
      nzOkText: 'Leave Module',
      nzCancelText: 'Stay',
      nzIconType: '',
      nzClosable: false,
      nzOnOk: () => {
        this.flow.passPopup();
        window.location.href = `/course-modules/${this.courseId}/list`;
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (!this.alreadyCompleted) {
      $event.preventDefault();
      $event.returnValue = '';
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.submit();
    }
  }

  ngOnDestroy(): void {
    if (this.progressIntervalId !== null) {
      clearInterval(this.progressIntervalId);
      this.progressIntervalId = null;
    }
    if (this.timerIntervalId !== null) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
    this.iframeDirObserver?.disconnect();
    this.iframeDirObserver = null;
    this.setBackgroundInert(false);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
